/**
 * SignalR connection helper — leader-elected, cross-tab.
 *
 * Goal: only ONE browser tab per session holds the SignalR connection. Other
 * tabs receive events via the existing BroadcastChannel that notificationsService
 * already uses for cross-tab coordination.
 *
 * Lifecycle
 *   - getSignalrHub(name) returns a tiny wrapper exposing on(eventName, fn).
 *     The wrapper works the same whether this tab is the leader (events come
 *     from the live HubConnection) or a follower (events come from BroadcastChannel).
 *   - The leader opens the underlying HubConnection lazily on first subscription.
 *   - Token is read from localStorage("jwt") on every reconnect via accessTokenFactory.
 *
 * Hubs supported (Phase 1 = notifications only; chat hub added in Phase 2):
 *   - notifications  → /hubs/notifications
 *   - erp-chat       → /hubs/erp-chat
 */
import { HubConnectionBuilder, HttpTransportType, LogLevel } from "@microsoft/signalr";

const HUBS = {
  notifications: {
    path: "/hubs/notifications",
    events: ["notification.created"],
  },
  "erp-chat": {
    path: "/hubs/erp-chat",
    events: [
      "chat.message.new",
      "chat.message.read",
      "chat.typing",
      "chat.conversation.updated",
      "presence.online",
      "presence.offline",
    ],
  },
};

const CHANNEL_NAME = "tirupati:signalr";
const LOCK_NAME = "tirupati:signalr:leader";

class SignalrManager {
  constructor() {
    this.hubs = {};               // hubName -> { connection, started }
    this.handlers = {};           // hubName -> Map<eventName, Set<fn>>
    this.channel = null;
    this.isLeader = false;
    this._leaderResolved = false;
    this._leaderResolveWaiters = [];

    this._initChannel();
    this._initLeaderElection();
  }

  /** Wait until leader election finishes (so callers know whether to expect direct events or broadcast events). */
  async whenReady() {
    if (this._leaderResolved) return;
    await new Promise((resolve) => this._leaderResolveWaiters.push(resolve));
  }

  /**
   * Subscribe to a hub event. Works for leader (real hub) and follower (channel).
   * Returns an unsubscribe function.
   */
  on(hubName, eventName, fn) {
    if (!HUBS[hubName]) throw new Error(`Unknown hub: ${hubName}`);
    if (!this.handlers[hubName]) this.handlers[hubName] = new Map();
    const map = this.handlers[hubName];
    const set = map.get(eventName) || new Set();
    set.add(fn);
    map.set(eventName, set);

    // Lazily open the connection on the leader the first time someone subscribes.
    this._maybeStart(hubName).catch(() => { /* swallowed: best-effort */ });

    return () => {
      const s = map.get(eventName);
      if (s) s.delete(fn);
    };
  }

  /** Invoke a server method (only valid on the leader). Followers reject. */
  async invoke(hubName, method, ...args) {
    if (!this.isLeader) {
      throw new Error("invoke() is only available on the leader tab");
    }
    const entry = await this._maybeStart(hubName);
    return entry.connection.invoke(method, ...args);
  }

  // ── Internal ─────────────────────────────────────────────────────────────

  _emitLocal(hubName, eventName, payload) {
    const map = this.handlers[hubName];
    if (!map) return;
    const set = map.get(eventName);
    if (!set) return;
    for (const fn of set) {
      try { fn(payload); } catch { /* ignore listener errors */ }
    }
  }

  _initChannel() {
    if (typeof BroadcastChannel === "undefined") return;
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (e) => {
      const { hub, event, payload } = e.data || {};
      if (!hub || !event) return;
      this._emitLocal(hub, event, payload);
    };
  }

  _broadcast(hubName, eventName, payload) {
    if (!this.channel) return;
    try {
      this.channel.postMessage({ hub: hubName, event: eventName, payload });
    } catch { /* ignore */ }
  }

  _initLeaderElection() {
    if (typeof navigator === "undefined" || !navigator.locks) {
      // No Web Locks support → every tab acts as its own leader (legacy behavior).
      this._markLeaderResolved(true);
      return;
    }
    navigator.locks.request(LOCK_NAME, { mode: "exclusive" }, async () => {
      this._markLeaderResolved(true);
      // Hold the lock for the lifetime of this tab. Browser releases on unload.
      await new Promise(() => {});
    });
    // If we don't get the lock immediately, we're a follower for now. Resolve
    // ready state on the next microtask so subscribers don't deadlock.
    setTimeout(() => {
      if (!this._leaderResolved) this._markLeaderResolved(false);
    }, 0);
  }

  _markLeaderResolved(isLeader) {
    if (this._leaderResolved) return;
    this.isLeader = isLeader;
    this._leaderResolved = true;
    for (const w of this._leaderResolveWaiters) w();
    this._leaderResolveWaiters = [];
  }

  async _maybeStart(hubName) {
    let entry = this.hubs[hubName];
    if (entry) {
      if (entry.started) return entry;
      // Another caller is mid-start; await its promise.
      await entry.startPromise;
      return entry;
    }
    entry = { connection: null, started: false, startPromise: null };
    this.hubs[hubName] = entry;

    await this.whenReady();
    if (!this.isLeader) {
      // Followers never open connections. Mark "started" so we don't retry.
      entry.started = true;
      return entry;
    }

    const cfg = HUBS[hubName];
    const baseUrl = (process.env.VUE_APP_API_BASE_URL || "http://localhost:5293/api").replace(/\/api\/?$/, "");
    const connection = new HubConnectionBuilder()
      .withUrl(baseUrl + cfg.path, {
        accessTokenFactory: () => localStorage.getItem("jwt") || "",
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    // Wire every declared event so local subscribers + cross-tab followers receive it.
    for (const eventName of cfg.events) {
      connection.on(eventName, (payload) => {
        this._emitLocal(hubName, eventName, payload);
        this._broadcast(hubName, eventName, payload);
      });
    }

    entry.connection = connection;
    entry.startPromise = connection.start().then(
      () => { entry.started = true; },
      (err) => {
        // Logged as warning; subscribers will simply not receive realtime events
        // until reconnect. The polling fallback in notificationsService still works.
        console.warn(`SignalR ${hubName} start failed:`, err);
        entry.started = true; // don't loop trying
      }
    );
    await entry.startPromise;
    return entry;
  }
}

let _instance = null;
export function getSignalrManager() {
  if (!_instance) _instance = new SignalrManager();
  return _instance;
}
